import React from 'react';
import Layout from '../components/Layout';

const PrivacyPolicy: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Politique de Confidentialité',
    description:
      'Politique de confidentialité de Primorixx. Découvrez comment nous collectons, utilisons et protégeons vos informations personnelles.',
    url: 'https://primorixx.com/politique-confidentialite',
  };

  return (
    <Layout
      title="Politique de Confidentialité"
      description="Politique de confidentialité de Primorixx. Découvrez comment nous collectons, utilisons et protégeons vos informations personnelles."
      canonicalUrl="/politique-confidentialite"
      jsonLd={jsonLd}
    >
      <div className="min-h-screen pt-20">
        <section className="py-20 bg-gradient-to-b from-dark-bg to-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text">
                Politique de Confidentialité
              </h1>
              <p className="text-lg text-dark-muted mb-8">
                Dernière mise à jour: {new Date().toLocaleDateString('fr-FR')}
              </p>

              <div className="bg-dark-card border border-dark-border rounded-xl p-8 space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    1. Introduction
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Primorixx ("nous", "notre" ou "entreprise") s'engage à
                    protéger la vie privée de nos utilisateurs. Cette
                    Politique de Confidentialité explique comment nous collectons, utilisons,
                    divulguons et protégeons vos informations personnelles lorsque
                    vous utilisez notre site.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    2. Informations que Nous Collectons
                  </h2>
                  <p className="text-dark-muted leading-relaxed mb-4">
                    Nous collectons les types d'informations suivants:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-dark-muted">
                    <li>
                      Informations fournies directement par vous, comme le nom et
                      l'adresse e-mail
                    </li>
                    <li>
                      Informations collectées automatiquement, y compris les données d'
                      utilisation et les cookies
                    </li>
                    <li>
                      Informations de tiers, comme les données de partenaires
                      commerciaux
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    3. Comment Nous Utilisons Vos Informations
                  </h2>
                  <p className="text-dark-muted leading-relaxed mb-4">
                    Nous utilisons les informations collectées pour:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-dark-muted">
                    <li>Fournir et améliorer nos services</li>
                    <li>Personnaliser votre expérience sur le site</li>
                    <li>Envoyer des communications liées à nos services</li>
                    <li>Analyser l'utilisation du site et générer des statistiques</li>
                    <li>Prévenir les fraudes et garantir la sécurité</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    4. Partage d'Informations
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Nous ne vendons pas vos informations personnelles. Nous pouvons partager
                    des informations avec des tiers uniquement dans les situations suivantes:
                    avec votre consentement, pour respecter les obligations légales, avec
                    des prestataires de services qui nous aident dans l'exploitation du site,
                    ou en cas de fusion ou d'acquisition de l'entreprise.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    5. Cookies
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Nous utilisons des cookies et des technologies similaires pour améliorer votre
                    expérience. Pour plus d'informations, consultez notre{' '}
                    <a
                      href="/politique-cookies"
                      className="text-dark-accent hover:underline"
                    >
                      Politique de Cookies
                    </a>
                    .
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    6. Vos Droits
                  </h2>
                  <p className="text-dark-muted leading-relaxed mb-4">
                    Vous avez le droit de:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-dark-muted">
                    <li>Accéder à vos informations personnelles</li>
                    <li>Corriger les informations inexactes</li>
                    <li>Demander la suppression de vos données</li>
                    <li>Vous opposer au traitement de vos données</li>
                    <li>Demander la portabilité de vos données</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    7. Sécurité
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles
                    appropriées pour protéger vos informations personnelles contre
                    l'accès non autorisé, la modification, la divulgation ou la destruction.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    8. Conservation des Données
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Nous conservons vos informations personnelles uniquement le temps
                    nécessaire pour remplir les objectifs décrits dans cette
                    politique, sauf si une période de conservation plus longue est
                    exigée ou permise par la loi.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    9. Modifications de Cette Politique
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Nous pouvons mettre à jour cette Politique de Confidentialité
                    périodiquement. Nous vous informerons des modifications significatives
                    en publiant la nouvelle politique sur cette page et en mettant à jour la date
                    de "dernière mise à jour".
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    10. Contact
                  </h2>
                  <p className="text-dark-muted leading-relaxed">
                    Si vous avez des questions sur cette Politique de Confidentialité, contactez-nous
                    par e-mail: contact@primorixx.com
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;

